import { useMemo } from 'react';
import { MdInfoOutline } from 'react-icons/md';
import { Chip, Tooltip, useDisclosure } from '@ds';
import { SectionCard, SectionRow, TabSection } from '@features/components/CaseOverview/components';
import { useAuthContext } from '@lp/core/auth';
import { CaseDetailsPermissions } from '@/permissions';
import { Case, CaseStatus, ServiceType } from '@/api/apis/case';
import { RouteTypeEnum } from '@/api/apis/route';
import { useLogisticsById } from '@/queries/route-planner';
import { useDeviceTrackingByCaseIdQuery } from '@/queries/device-tracking';
import { getIsEuropeanRegion, isAllowedStatus, yesNo } from '@/utils/helpers';
import { checkTxCenterProvidersInTransit } from '@/utils/route';
import { EditLocationTrackingModal } from '../components/LocationTracking/EditLocationTrackingModal';

const DEVICE_TOOLTIP_CONTENT = (
  <div className="max-w-[320px]">
    <span className="text-sm leading-[140%]">The OCS device selected for location tracking on the TX Center side</span>
  </div>
);

type LocationTrackingProps = {
  caseData: Case;
};

export const LocationTracking = ({ caseData }: LocationTrackingProps) => {
  const { caseId, status, serviceType, logisticsReference, trialProgram, serviceRegionId } = caseData;
  const { checkPermission } = useAuthContext();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: logisticsData } = useLogisticsById(logisticsReference?.id);

  const isTrialCase = !!trialProgram;
  const isEuropeanRegion = getIsEuropeanRegion(serviceRegionId);
  const mainRouteLegs = logisticsData?.routes?.find((r) => r.routeType === RouteTypeEnum.Main)?.legs || [];
  const isTxCenterProvider = checkTxCenterProvidersInTransit(mainRouteLegs, 'raw');
  const showForRole = checkPermission(CaseDetailsPermissions.LocationTrackingRead);
  const showForStatus = isAllowedStatus({
    statusFrom: CaseStatus.ResourcesBooked,
    statusTo: CaseStatus.ReturningToBase,
    currentStatus: status,
  });
  const withLocationTracking = Boolean(
    logisticsData?.isTmdxDriveCompatible &&
    showForRole &&
    showForStatus &&
    !isTrialCase &&
    !isEuropeanRegion &&
    !isTxCenterProvider,
  );

  const { data: deviceTracking } = useDeviceTrackingByCaseIdQuery(caseId, false);

  const displaySettings = deviceTracking?.displaySettings;
  const deviceToDisplay = displaySettings?.deviceToDisplay;
  const graphs = deviceTracking?.graphs;
  const selectedDeviceName = useMemo(
    () => graphs?.find((graph) => graph.device.id === deviceToDisplay)?.device.name ?? '-',
    [graphs, deviceToDisplay],
  );

  if (!withLocationTracking) return null;

  const isDirectAcquisition = serviceType === ServiceType.DirectAcquisition;
  const hasDAPermission = checkPermission(CaseDetailsPermissions.CaseDARetrievalUpdate);
  const hasTrackingPermission = checkPermission(CaseDetailsPermissions.LocationTrackingUpdate);
  const hasEditPermission = hasTrackingPermission || (isDirectAcquisition && hasDAPermission);

  return (
    <TabSection onSectionEdit={hasEditPermission ? onOpen : undefined} title="Location Tracking">
      {isOpen && deviceTracking && (
        <EditLocationTrackingModal isOpen={isOpen} onClose={onClose} deviceTracking={deviceTracking} />
      )}
      <SectionCard>
        <SectionRow label="Enable TX Center ETA">
          {displaySettings ? <Chip size="sm">{yesNo(displaySettings.displayEstimatedTimeOfArrival)}</Chip> : '-'}
        </SectionRow>
        <SectionRow label="Enable Location Tracking">
          {displaySettings ? <Chip size="sm">{yesNo(displaySettings.displayLocationTracking)}</Chip> : '-'}
        </SectionRow>
        <div className="grid grid-cols-2 gap-4 items-center">
          <span className="text-base-20 text-m-r flex items-center gap-2">
            OCS Device for Tracking
            <Tooltip content={DEVICE_TOOLTIP_CONTENT}>
              <div>
                <MdInfoOutline size={16} className="text-base-25" />
              </div>
            </Tooltip>
          </span>
          <div className="text-m-m truncate">{selectedDeviceName}</div>
        </div>
      </SectionCard>
    </TabSection>
  );
};
